/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   ManagementTab.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US1)
 *     Description     :   The admin Management surface — curate every pipeline: importance
 *                         tier, lifecycle (retire / reactivate / adopt), an ADMIN-EDITABLE
 *                         cadence override (FR-016a), plus last-run and dormancy signals.
 *                         Blueprint DataTable (consistent typography, content-sized columns).
 *                         Readable by every role so the curated state explains the dashboard's
 *                         rankings (FR-009a); write controls are inert for non-admins
 *                         (enforcement is server-side, FR-009). Kit + tokens only.
 *     Constitution    :   Principle XLV (design system), XXXVII (headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { useState } from 'react';
import { Pencil, Check, X, Link2 } from 'lucide-react';
import { Card, DataTable, Badge, Button, Select, Toggle, EmptyState, LoadingState, ErrorState } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { AppModal } from '@/components/ui/AppModal';
import { useAuth } from '@/hooks/useAuth';
import { usePipelineCuration, useCurationMutations } from '@/hooks/usePipelineMonitoring';
import type { CurationRow, ImportanceTier, LifecycleState } from '@shared/types/pipelineMonitoring';
import { SearchableSelect } from './SearchableSelect';
import { roleSatisfies } from '../../../../../shared/access/roles';

const TIERS: ImportanceTier[] = ['Critical', 'Standard', 'Low'];

function lifecycleBadge(s: LifecycleState): JSX.Element {
  const variant = s === 'active' ? 'success' : s === 'retired' ? 'default' : s === 'pending' ? 'warning' : 'danger';
  return <Badge variant={variant}>{s}</Badge>;
}
function statusVariant(s: string): 'success' | 'danger' | 'warning' | 'default' {
  const u = s.toUpperCase();
  if (['FAILED', 'FAIL', 'ABANDONED', 'ERROR', 'COMPLETED_WITH_ERRORS'].includes(u)) return 'danger';
  if (u === 'PARTIAL') return 'warning';
  if (s === '(unknown)' || ['NO_OP', 'NOOP', 'SKIPPED'].includes(u)) return 'default';
  return 'success';
}
function fmtCadence(r: CurationRow): string {
  if (r.cadence.overrideMinutes != null) return `${r.cadence.overrideMinutes}m (override)`;
  if (r.cadence.inferredSeconds != null) return `~${Math.round(r.cadence.inferredSeconds / 60)}m (inferred)`;
  return 'not yet known';
}
function relTime(iso: string | null): string {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function slugDiffers(name: string, slug: string): boolean {
  const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm(name) !== norm(slug);
}

/** Admin-editable cadence (FR-016a): shows the inferred/override cadence with an inline editor. */
function CadenceCell({ row, isAdmin, disabled, onSave }: {
  row: CurationRow; isAdmin: boolean; disabled: boolean; onSave: (minutes: number | null) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  if (editing) {
    const commit = (): void => {
      const t = val.trim();
      if (t === '') { onSave(null); setEditing(false); return; }   // clear ⇒ revert to inferred
      const n = Number(t);
      if (!isNaN(n) && n > 0) onSave(Math.round(n));
      setEditing(false);
    };
    return (
      <div className="flex items-center gap-1">
        <input
          type="number" min={1} value={val} autoFocus disabled={disabled} placeholder="min"
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-1.5 py-0.5 text-xs text-[var(--color-text-primary)]"
        />
        <button type="button" aria-label="Save cadence" onClick={commit} className="text-[var(--color-status-success)] hover:opacity-80"><Check size={14} /></button>
        <button type="button" aria-label="Cancel" onClick={() => setEditing(false)} className="text-[var(--color-text-secondary)] hover:opacity-80"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[var(--color-text-secondary)]">{fmtCadence(row)}</span>
      <button
        type="button" aria-label="Edit cadence" disabled={disabled || !isAdmin}
        onClick={() => { setVal(row.cadence.overrideMinutes != null ? String(row.cadence.overrideMinutes) : ''); setEditing(true); }}
        className="text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] disabled:opacity-40"
      >
        <Pencil size={12} />
      </button>
    </div>
  );
}

export function ManagementTab(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = roleSatisfies(user?.role, ['Admin']);
  const [showRetired, setShowRetired] = useState(false);
  const [linkTarget, setLinkTarget] = useState<CurationRow | null>(null);
  const [priorSel, setPriorSel] = useState('');
  const { data, isLoading, isError, refetch } = usePipelineCuration(showRetired);
  const { patch, link, unlink } = useCurationMutations();

  if (isLoading) return <LoadingState />;
  if (isError || !data) return <ErrorState onRetry={() => void refetch()} />;

  const busy = patch.isPending || link.isPending || unlink.isPending;
  const mutError = (patch.error || link.error || unlink.error) as { response?: { data?: { error?: string } } } | undefined;
  const closeLink = (): void => { setLinkTarget(null); setPriorSel(''); };
  const setTier = (appInstance: string, importanceTier: ImportanceTier): void => { patch.mutate({ appInstance, patch: { importanceTier } }); };
  const setLifecycle = (appInstance: string, lifecycleState: LifecycleState): void => { patch.mutate({ appInstance, patch: { lifecycleState } }); };
  const setCadence = (appInstance: string, cadenceOverrideMinutes: number | null): void => { patch.mutate({ appInstance, patch: { cadenceOverrideMinutes } }); };

  const columns: DataTableColumn<CurationRow>[] = [
    { key: 'appName', header: 'Pipeline', width: '20%', render: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-[var(--color-text-primary)]">{r.appName}</div>
        {slugDiffers(r.appName, r.appInstance) && (
          <div className="truncate font-mono text-[11px] text-[var(--color-text-secondary)]">{r.appInstance}</div>
        )}
        {r.identityLink && (
          <div className="flex items-center gap-1 truncate text-[11px] text-[var(--color-text-secondary)]">
            <Link2 size={10} className="flex-shrink-0" /> continues {r.identityLink.priorAppInstance}
          </div>
        )}
      </div>
    ) },
    { key: 'lifecycleState', header: 'State', width: '11%', sortValue: (r) => r.lifecycleState, render: (r) => (
      <div className="flex items-center gap-1.5">
        {lifecycleBadge(r.lifecycleState)}
        {r.lifecycleState === 'active' && r.isDormant && <Badge variant="warning" size="xs">dormant</Badge>}
      </div>
    ) },
    { key: 'importanceTier', header: 'Importance', width: '13%', sortValue: (r) => r.importanceTier, render: (r) => (
      // FR-009a: the control is present for every role, but inert (disabled) for non-admins.
      <div className="w-28">
        <Select value={r.importanceTier} disabled={busy || !isAdmin} selectSize="sm"
          onChange={(e) => setTier(r.appInstance, e.target.value as ImportanceTier)}
          options={TIERS.map((t) => ({ value: t, label: t }))} />
      </div>
    ) },
    { key: 'cadence', header: 'Cadence', width: '15%', sortable: false, render: (r) => (
      <CadenceCell row={r} isAdmin={isAdmin} disabled={busy} onSave={(m) => setCadence(r.appInstance, m)} />
    ) },
    { key: 'lastRun', header: 'Last run', width: '13%', sortValue: (r) => r.lastRunAt, render: (r) => (
      r.lastRunStatus ? (
        <div className="min-w-0">
          <Badge variant={statusVariant(r.lastRunStatus)} size="xs">{r.lastRunStatus}</Badge>
          <div className="truncate text-[11px] text-[var(--color-text-secondary)]">{relTime(r.lastRunAt)}</div>
        </div>
      ) : <span className="text-[var(--color-text-secondary)]">—</span>
    ) },
    { key: 'runCount', header: 'Runs', width: '8%', align: 'right', sortValue: (r) => r.runCount, render: (r) => r.runCount.toLocaleString() },
    { key: 'failRate', header: 'Fail %', width: '8%', align: 'right', sortValue: (r) => r.failRate, render: (r) => (
      // Spec 298 FR-004: a pipeline with no runs has no fail rate; it says so, never "0.0%"
      r.failRate === null ? <span className="text-[var(--color-text-secondary)]">no runs</span>
        : <span className={r.failRate > 0 ? 'text-[var(--color-status-danger)]' : undefined}>{(r.failRate * 100).toFixed(1)}%</span>
    ) },
    // FR-009a: curation controls are PRESENT for every role but inoperative (disabled) for
    // non-admins — enforcement is on the request server-side; the UI never simply omits them.
    { key: 'actions', header: '', align: 'right', sortable: false, render: (r) => {
      const off = busy || !isAdmin;
      return (
        <div className="flex justify-end gap-2">
          {r.lifecycleState === 'pending' && (
            <Button size="sm" variant="primary" disabled={off} onClick={() => setLifecycle(r.appInstance, 'active')}>Adopt</Button>
          )}
          {(r.lifecycleState === 'retired' || r.lifecycleState === 'reappeared') && (
            <Button size="sm" variant="secondary" disabled={off} onClick={() => setLifecycle(r.appInstance, 'active')}>Reactivate</Button>
          )}
          {r.lifecycleState !== 'retired' && (
            <Button size="sm" variant="outline" disabled={off} onClick={() => setLifecycle(r.appInstance, 'retired')}>Retire</Button>
          )}
          {r.identityLink ? (
            <Button size="sm" variant="outline" disabled={off} onClick={() => unlink.mutate(r.appInstance)}>Unlink</Button>
          ) : (
            <Button size="sm" variant="outline" disabled={off} leftIcon={<Link2 className="h-3 w-3" />} onClick={() => { setLinkTarget(r); setPriorSel(''); }}>Link</Button>
          )}
        </div>
      );
    } },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {isAdmin ? 'Curate every pipeline — set its importance, cadence, and lifecycle. Retiring stops it counting and alerting; history is preserved.'
                   : 'The curated state of every pipeline. Only administrators can change it.'}
        </p>
        <label className="flex flex-shrink-0 items-center gap-2 text-sm">
          <Toggle checked={showRetired} onChange={setShowRetired} aria-label="Show retired" />
          <span className="text-[var(--color-text-secondary)]">Show retired</span>
        </label>
      </div>

      {(patch.isError || link.isError || unlink.isError) && (
        <Card className="border-[var(--color-status-danger)]">
          <span className="text-sm text-[var(--color-status-danger)]">
            {mutError?.response?.data?.error ?? 'Curation change failed.'}
          </span>
        </Card>
      )}

      {data.rows.length === 0 ? (
        <EmptyState title="No pipelines to manage" message="No pipelines are reporting yet, or all are hidden. Toggle “Show retired” to see retired ones." />
      ) : (
        <Card padding="none">
          <DataTable columns={columns} rows={data.rows} rowKey={(r) => r.appInstance} minWidth="60rem" />
        </Card>
      )}

      {/* Identity link (FR-017b): declare a renamed pipeline is the same as a prior identity. */}
      {linkTarget && (
        <AppModal
          open onClose={closeLink} title="Link identity" size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeLink}>Cancel</Button>
              <Button variant="primary" disabled={!priorSel || link.isPending}
                onClick={() => { link.mutate({ appInstance: linkTarget.appInstance, prior: priorSel }); closeLink(); }}>
                Link
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Declare that <span className="font-medium text-[var(--color-text-primary)]">{linkTarget.appName}</span> is
              the same pipeline as a prior identity that reported under a different name. Its run history and importance
              tier then read as continuous across the rename, and the prior identity stops raising a dormancy alert.
              This is never auto-detected — it is a deliberate, audited decision.
            </p>
            <div>
              <label className="mb-1 block text-xs text-[var(--color-text-secondary)]">Prior identity</label>
              <SearchableSelect
                options={data.rows.filter((r) => r.appInstance !== linkTarget.appInstance)
                  .map((r) => ({ value: r.appInstance, label: slugDiffers(r.appName, r.appInstance) ? `${r.appName} (${r.appInstance})` : r.appName }))}
                value={priorSel} onChange={setPriorSel} placeholder="Search pipelines…" allLabel="— none —" />
            </div>
          </div>
        </AppModal>
      )}
    </div>
  );
}

export default ManagementTab;
